/**
 * Fee Schedule — the firm-wide tenant fee amounts. Retires the old per-lease
 * Lease.lateFeeAmount/lateFeeGraceDays override: one policy for every
 * lease, admin-editable here instead of set on each lease individually.
 *
 * Single singleton table (`fee_schedule`), same shape as CompanyInformationPage's
 * two tables. GET is any authenticated staff member; PATCH is `settings:write`,
 * ADMIN only — "hiding is not protecting," same stance every other staff nav
 * link takes: this page renders for anyone who reaches it and only the save
 * button is refused for a non-admin.
 *
 * Only the four dollar amounts are editable here. The day-5/day-10/6-consecutive-
 * month timing is fixed in code (jobs/monthly-charges.ts), by product decision.
 */

import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router';
import type { FeeScheduleDto } from 'shared';
import { api } from '../../lib/api.js';
import { Field, FormErrors, TextInput } from '../../components/Form.js';

interface FormState {
  lateFee5DayAmount: string;
  lateFee10DayAmount: string;
  habitualLateFeeAmount: string;
  nsfFeeAmount: string;
}

const EMPTY: FormState = {
  lateFee5DayAmount: '',
  lateFee10DayAmount: '',
  habitualLateFeeAmount: '',
  nsfFeeAmount: '',
};

export function FeeSchedulePage() {
  const navigate = useNavigate();

  const [form, setForm] = useState<FormState>(EMPTY);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void api<FeeScheduleDto>('/settings/fee-schedule')
      .then((schedule) => {
        if (cancelled) return;
        setForm({
          lateFee5DayAmount: schedule.lateFee5DayAmount,
          lateFee10DayAmount: schedule.lateFee10DayAmount,
          habitualLateFeeAmount: schedule.habitualLateFeeAmount,
          nsfFeeAmount: schedule.nsfFeeAmount,
        });
        setLoaded(true);
      })
      .catch((caught: unknown) => {
        if (!cancelled) setLoadError(caught);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const set =
    <K extends keyof FormState>(key: K) =>
    (value: FormState[K]) =>
      setForm((previous) => ({ ...previous, [key]: value }));

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setSaveError(null);
    setSaved(false);
    try {
      const updated = await api<FeeScheduleDto>('/settings/fee-schedule', {
        method: 'PATCH',
        body: JSON.stringify(form),
      });
      setForm({
        lateFee5DayAmount: updated.lateFee5DayAmount,
        lateFee10DayAmount: updated.lateFee10DayAmount,
        habitualLateFeeAmount: updated.habitualLateFeeAmount,
        nsfFeeAmount: updated.nsfFeeAmount,
      });
      setSaved(true);
    } catch (caught) {
      setSaveError(caught);
    } finally {
      setBusy(false);
    }
  }

  if (loadError) return <FormErrors error={loadError} />;
  if (!loaded) return <p className="muted">Loading…</p>;

  return (
    <section className="stack">
      <div className="row">
        <h1>Fee Schedule</h1>
        <span className="shell__spacer" />
        <button
          type="button"
          className="button button--quiet"
          onClick={() => void navigate('/staff')}
        >
          ← Dashboard
        </button>
      </div>

      <p className="muted">
        Fees charged to tenants only — never owners. These amounts are retained by the company and
        excluded from every owner statement. Only the dollar amounts are configurable here; when
        each fee triggers (5 days late, 10 days late, and the 6th consecutive late month) is fixed.
      </p>

      <form className="stack card" onSubmit={(event) => void submit(event)}>
        <FormErrors error={saveError} />

        <Field
          label="Late fee — 5 days late"
          name="lateFee5DayAmount"
          hint="Charged once rent is 5 days past due."
        >
          <TextInput
            name="lateFee5DayAmount"
            type="number"
            min="0"
            step="0.01"
            value={form.lateFee5DayAmount}
            onChange={set('lateFee5DayAmount')}
          />
        </Field>

        <Field
          label="Late fee — 10 days late (additional)"
          name="lateFee10DayAmount"
          hint="Charged in addition to the 5-day fee if rent is still unpaid at 10 days late."
        >
          <TextInput
            name="lateFee10DayAmount"
            type="number"
            min="0"
            step="0.01"
            value={form.lateFee10DayAmount}
            onChange={set('lateFee10DayAmount')}
          />
        </Field>

        <Field
          label="Habitual late fee (6th consecutive late month)"
          name="habitualLateFeeAmount"
          hint="Charged once when a lease is late for the 6th month in a row. Resets after an on-time month."
        >
          <TextInput
            name="habitualLateFeeAmount"
            type="number"
            min="0"
            step="0.01"
            value={form.habitualLateFeeAmount}
            onChange={set('habitualLateFeeAmount')}
          />
        </Field>

        <Field
          label="NSF / returned payment fee"
          name="nsfFeeAmount"
          hint="Charged on a returned check, cash discrepancy, or disputed ACH payment."
        >
          <TextInput
            name="nsfFeeAmount"
            type="number"
            min="0"
            step="0.01"
            value={form.nsfFeeAmount}
            onChange={set('nsfFeeAmount')}
          />
        </Field>

        {saved ? <p className="muted">Saved.</p> : null}

        <div className="row">
          <button type="submit" className="button" disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </form>
    </section>
  );
}
